import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../../shared/application/useDebouncedValue";
import type { AssetTypesListQuery, AssetTypesRepository } from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface AssetTypeOption {
  readonly value: string;
  readonly label: string;
}

export interface UseAssetTypeOptionsResult {
  readonly options: readonly AssetTypeOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated asset-type options for the `Combobox` async
 * mode -- mirrors useAssetOptions. `accountId` scopes the list for a staff
 * caller (a client caller always gets their own account's types). Only
 * active types are offered (filtered here). `onlyWithAssets` leaves out the
 * types no asset has yet, on the server -- for pickers where such a type
 * would be refused (an alert's assignment).
 */
export function useAssetTypeOptions(
  repository: AssetTypesRepository | undefined,
  accountId?: string,
  { onlyWithAssets = false }: { readonly onlyWithAssets?: boolean } = {},
): UseAssetTypeOptionsResult {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith("ar");
  const sortBy = isAr ? "nameAr" : "nameEn";

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const isEnabled =
    repository !== undefined && typeof repository.list === "function";

  const listQuery = (page: number): AssetTypesListQuery => ({
    page,
    limit: PAGE_SIZE,
    sort: "asc",
    sortBy,
    ...(search === undefined ? {} : { search }),
    ...(accountId === undefined ? {} : { accountId }),
    ...(onlyWithAssets ? { onlyWithAssets: true } : {}),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.assetTypes.list({
      limit: PAGE_SIZE,
      search,
      sort: "asc",
      sortBy,
      accountId,
      onlyWithAssets,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: isEnabled,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data
          .filter((assetType) => assetType.status === "ACTIVE")
          .map((assetType) => ({
            value: assetType.id,
            label: isAr
              ? assetType.nameAr || assetType.nameEn
              : assetType.nameEn || assetType.nameAr,
          })),
      ),
    [query.data, isAr],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
